import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
} from 'convex/server'
import { convexToJson, type Value } from 'convex/values'

/**
 * A server-read snapshot of ONE Convex query, carried to the client together
 * with the exact args it was read with. The client subscribes to the same
 * query and paints the seed only while its own args match (see
 * `useSeededAuthedQuery`), so a seed can never stand in for a different
 * window, filter or record.
 *
 * Server pages build it with `settleSeed`
 * (`lib/data/convex/prefetchable-query.ts`) around a prefetchable
 * `'use cache: private'` reader: a link prefetch then carries the seed in its
 * App Shell and the destination paints its data in the first frame.
 */
export type QuerySeed<Query extends FunctionReference<'query'>> = Readonly<{
  args: FunctionArgs<Query>
  value: FunctionReturnType<Query>
}>

/**
 * Seeds for a `useAuthedQueries` request map (`useSeededAuthedQueries`),
 * keyed by the same request keys; each paints only for its own args.
 */
export type QuerySeeds = Readonly<
  Record<string, QuerySeed<FunctionReference<'query'>>>
>

/** Key-order-independent JSON of Convex args. */
function canonicalArgs(args: unknown): string {
  return JSON.stringify(sortKeys(convexToJson(args as Value)))
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeys)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortKeys((value as Record<string, unknown>)[key])])
    )
  }
  return value
}

/** True when `args` are the args the seed was read with. */
export function isSeedForArgs<Query extends FunctionReference<'query'>>(
  seed: QuerySeed<Query>,
  args: FunctionArgs<Query>
): boolean {
  return canonicalArgs(seed.args) === canonicalArgs(args)
}
